import { useMemo, SetStateAction, Dispatch } from 'react';
import { useIntl } from 'react-intl';

import in_ from '_common/utils/in';
import { openModal } from 'App/ModalContext/utils';
import { useOrderMetadataMutation } from 'App/redux/MetadataApi';

import { DATA_TYPE_TRANSLATIONS } from '../const';

import {
  Table as BaseTable,
  Cell,
  type TableColumn,
  type TableRow,
} from '_common/components/Table';

import NotApplicable from '../NotApplicable';

type MetadataRow = ApiSchemas['MetadataObjectSchema'];

type TableProps = {
  metadata: ApiSchemas['MetadataObjectSchema'][];
  selectedMetadata: ApiSchemas['MetadataObjectSchema'] | undefined;
  deleteMetadata: (id: string, name: string) => void;
  select: Dispatch<SetStateAction<number>>;
};

const Table = ({ metadata, selectedMetadata, deleteMetadata, select }: TableProps) => {
  const intl = useIntl();

  const [orderMetadata] = useOrderMetadataMutation();

  const handleMoveUp = (metadataId: string) => {
    // e.stopPropagation();
    console.log('up ', metadataId);
    orderMetadata({ object_id: metadataId, direction: 'up' });
  };
  const handleMoveDown = (metadataId: string) => {
    // e.stopPropagation();
    console.log('down ', metadataId);
    orderMetadata({ object_id: metadataId, direction: 'down' });
  };

  const edit = (metadata: ApiSchemas['MetadataObjectSchema'], editOptions?: boolean) => {
    if (!metadata.automatic) {
      openModal({
        modal: 'ManualMetadataModal',
        data: { metadata, editOptions },
      });
    }
  };

  const columns = useMemo<TableColumn<MetadataRow>[]>(() => {
    return [
      {
        id: 'name',
        accessorKey: 'id',
        enableSorting: false,
        minWidth: 167,
        header: intl.formatMessage({ id: 'global.name' }),
        cell: (info) => {
          const metadata = info.row.original;
          return <Cell testId={`${metadata.id}-name-column`}>{metadata.name}</Cell>;
        },
      },
      {
        id: 'dataType',
        accessorKey: 'data_type',
        enableSorting: false,
        size: 160,
        header: intl.formatMessage({ id: 'DATA_TYPE' }),
        cell: (info) => {
          const metadata = info.row.original;
          return (
            <Cell testId={`${metadata.id}-data-type-column`}>
              {in_(metadata.data_type, DATA_TYPE_TRANSLATIONS)
                ? DATA_TYPE_TRANSLATIONS[metadata.data_type]
                : metadata.data_type}
            </Cell>
          );
        },
      },
      {
        id: 'value',
        accessorKey: 'options',
        enableSorting: false,
        size: 160,
        header: intl.formatMessage({ id: 'VALUE' }),
        cell: (info) => {
          const metadata = info.row.original;
          return (
            <Cell testId={`${metadata.id}-value-column`}>
              {(metadata.data_type === 'select' || metadata.data_type === 'multi-select') &&
              metadata.options ? (
                metadata.options.map((option) => option.fields.join('-')).join(', ')
              ) : (
                <NotApplicable place="table" />
              )}
            </Cell>
          );
        },
      },
      {
        id: 'charLimit',
        accessorKey: 'char_limit',
        enableSorting: false,
        size: 120,
        header: intl.formatMessage({ id: 'CHARACTER_LIMIT' }),
        cell: (info) => {
          const metadata = info.row.original;
          return (
            <Cell testId={`${metadata.id}-character-limit-column`}>
              {metadata.data_type === 'string' ? (
                metadata.char_limit
              ) : (
                <NotApplicable place="table" />
              )}
            </Cell>
          );
        },
      },
      {
        id: 'required',
        accessorKey: 'required',
        enableSorting: false,
        size: 120,
        header: intl.formatMessage({ id: 'REQUIRED' }),
        cell: (info) => {
          const metadata = info.row.original;
          return (
            <Cell testId={`${metadata.id}-required-column`}>
              <Cell.Boolean value={metadata.required} testId={`${metadata.id}-required`} />
            </Cell>
          );
        },
      },
      {
        id: 'exportVeeva',
        accessorKey: 'confirm_pre_export',
        enableSorting: false,
        size: 120,
        header: intl.formatMessage({ id: 'EXPORT_TO_VEEVA' }),
        cell: (info) => {
          const metadata = info.row.original;
          return (
            <Cell testId={`${metadata.id}-export-to-veeva-column`}>
              <Cell.Boolean
                /**@ts-expect-error Not yet supported in the API side */
                value={metadata.confirm_pref_export}
                testId={`${metadata.id}-export-to-veeva`}
              />
            </Cell>
          );
        },
      },
    ];
  }, []);

  const rowsData = useMemo(() => {
    return metadata.map<TableRow<MetadataRow>>((m, index) => {
      const suffixActions: NonNullable<TableRow['suffix']>['actions'] = [
        {
          label: intl.formatMessage({ id: 'MOVE_METADATA_UP' }),
          disabled: index === 0,
          onClick: () => {
            handleMoveUp(m.id);
          },
        },
        {
          label: intl.formatMessage({ id: 'MOVE_METADATA_DOWN' }),
          disabled: index === metadata.length - 1,
          onClick: () => {
            handleMoveDown(m.id);
          },
        },
        {
          label: intl.formatMessage({ id: 'EDIT_METADATA' }),
          onClick: () => {
            edit(m);
          },
        },
      ];

      if (m.data_type === 'select' || m.data_type === 'multi-select') {
        suffixActions.push({
          label: intl.formatMessage({ id: 'EDIT_SELECT_OPTIONS' }),
          onClick: () => {
            edit(m, true);
          },
        });
      }

      suffixActions.push({
        label: intl.formatMessage({ id: 'DELETE_METADATA' }),
        onClick: (e) => {
          deleteMetadata(m.id, m.name);
        },
      });

      return {
        ...m,
        suffix: {
          actions: suffixActions,
        },
      };
    });
  }, [metadata]);

  return (
    <BaseTable
      testId="manual-metadata-table"
      options={{ columns, data: rowsData }}
      onRowClick={(row) => {
        select(row.index);
      }}
      maxHeight="75rem"
      selection={selectedMetadata ? { [selectedMetadata.id]: true } : {}}
      identity="manual_metadata"
      isLoading={false}
      loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
      renderFooter={() => null}
      selectable={false}
    />
  );
};
export default Table;
